import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.addEventListener('change', this.#onSubscriptionChange);
    this.addEventListener('click', this.#onSubscriptionPickerToggle);
    this.#initializeSubscriptionPickers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#onSubscriptionPickerToggle);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */

  #onSubscriptionChange = (event) => {
    const select = event.target;

    // Handle both old and new dropdown classes
    const isOldDropdown = select.classList.contains('cart-selling-plan-select');
    const isNewDropdown = select.classList.contains('cart-subscription-select');

    if (!isOldDropdown && !isNewDropdown) return;

    this.#syncSubscriptionPickerState(select);
    this.#collapseSubscriptionPickerForSelect(select);

    // Old dropdown (fallback for products without subscription config)
    if (isOldDropdown) {
      const sellingPlanId = select.value || null;
      const lineKey = select.dataset.lineKey;
      const quantity = Number(select.dataset.quantity);

      if (!lineKey) return;

      this.#updateSellingPlan({ lineKey, quantity, sellingPlanId });
      return;
    }

    // New dropdown (metaobject-based)
    const selectedOption = select.selectedOptions[0];
    const optionKey = selectedOption.value;
    const sellingPlanId = selectedOption.dataset.sellingPlanId || null;
    let targetVariantId = selectedOption.dataset.variantId || null;
    const isOneTime = selectedOption.dataset.isOneTime === 'true';
    const lineKey = select.dataset.lineKey;
    const currentVariantId = select.dataset.currentVariantId;
    const mainVariantId = select.dataset.mainVariantId;
    const quantity = Number(select.dataset.quantity);

    if (!lineKey) return;

    // If no specific variant specified, use main product's variant (for monthly/one-time)
    if (!targetVariantId && mainVariantId) {
      targetVariantId = mainVariantId;
    }

    // Check if variant needs to change
    const needsVariantSwap = targetVariantId && targetVariantId !== currentVariantId;

    if (needsVariantSwap) {
      // Remove old item, add new variant
      this.#swapCartItemVariant({
        oldLineKey: lineKey,
        newVariantId: targetVariantId,
        quantity,
        sellingPlanId: isOneTime ? null : sellingPlanId,
        select, // Pass select to revert on error
        previousOptionKey: select.dataset.previousOptionKey || optionKey
      });
    } else {
      // Same variant, just update selling plan
      this.#updateSellingPlan({
        lineKey,
        quantity,
        sellingPlanId: isOneTime ? null : sellingPlanId
      });
    }

    // Store current selection for error recovery
    select.dataset.previousOptionKey = optionKey;
  };

  #onSubscriptionPickerToggle = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest('[data-subscription-toggle]');
    if (!(toggle instanceof HTMLButtonElement)) return;

    const picker = toggle.closest('[data-subscription-picker]');
    if (!(picker instanceof HTMLElement)) return;

    const shouldExpand = !picker.classList.contains('is-expanded');
    this.#collapseAllSubscriptionPickers();
    this.#setSubscriptionPickerExpanded(picker, shouldExpand);
  };

  #initializeSubscriptionPickers() {
    const selects = this.querySelectorAll('.cart-selling-plan-select, .cart-subscription-select');

    for (const select of selects) {
      if (select instanceof HTMLSelectElement) {
        this.#syncSubscriptionPickerState(select);
      }
    }
  }

  #syncSubscriptionPickerState(select) {
    const picker = select.closest('[data-subscription-picker]');
    if (!(picker instanceof HTMLElement)) return;

    const selectedOption = select.selectedOptions[0];
    if (!selectedOption) return;

    const currentLabel = picker.querySelector('[data-subscription-current]');
    if (currentLabel instanceof HTMLElement) {
      currentLabel.textContent = selectedOption.textContent.trim();
    }

    const toggleLabel = picker.querySelector('[data-subscription-toggle-label]');
    if (toggleLabel instanceof HTMLElement) {
      const isOneTime = selectedOption.dataset.isOneTime === 'true' || selectedOption.value === '';
      const dynamicOneTimeCta = picker.dataset.oneTimeCta?.trim() || 'Subscribe & Save';
      toggleLabel.textContent = isOneTime ? dynamicOneTimeCta : selectedOption.textContent.trim();
      picker.classList.toggle('is-one-time', isOneTime);
      picker.classList.toggle('is-subscription', !isOneTime);
    }
  }

  #setSubscriptionPickerExpanded(picker, expanded) {
    picker.classList.toggle('is-expanded', expanded);

    const toggle = picker.querySelector('[data-subscription-toggle]');
    if (toggle instanceof HTMLButtonElement) {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  #collapseSubscriptionPickerForSelect(select) {
    const picker = select.closest('[data-subscription-picker]');
    if (picker instanceof HTMLElement) {
      this.#setSubscriptionPickerExpanded(picker, false);
    }
  }

  #collapseAllSubscriptionPickers() {
    const expandedPickers = this.querySelectorAll('[data-subscription-picker].is-expanded');
    for (const picker of expandedPickers) {
      if (picker instanceof HTMLElement) {
        this.#setSubscriptionPickerExpanded(picker, false);
      }
    }
  }

  #updateSellingPlan({ lineKey, quantity, sellingPlanId }) {
    this.#disableCartItems();

    const sectionsToUpdate = new Set([this.sectionId]);

    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: lineKey,
      quantity,
      selling_plan: sellingPlanId,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }))
      .then((res) => res.json())
      .then((parsedResponse) => {
        if (parsedResponse.errors) {
          console.error(parsedResponse.errors);
          return;
        }

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: parsedResponse.item_count,
            source: 'selling-plan-change',
            sections: parsedResponse.sections,
          })
        );

        morphSection(this.sectionId, parsedResponse.sections[this.sectionId]);
      })
      .catch(console.error)
      .finally(() => {
        this.#enableCartItems();
      });
  }

  /**
   * Swaps a cart item to a different variant (for prepaid bundles)
   * @param {Object} config
   * @param {string} config.oldLineKey - Current line item key
   * @param {string} config.newVariantId - Target variant ID
   * @param {number} config.quantity - Quantity to maintain
   * @param {string|null} config.sellingPlanId - Selling plan ID (if any)
   * @param {HTMLSelectElement} config.select - Dropdown element (for error recovery)
   * @param {string} config.previousOptionKey - Previous option key (for revert)
   */
  #swapCartItemVariant({ oldLineKey, newVariantId, quantity, sellingPlanId, select, previousOptionKey }) {
    this.#disableCartItems();

    const sectionsToUpdate = new Set([this.sectionId]);

    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sections = Array.from(sectionsToUpdate).join(',');
    const sectionsUrl = window.location.pathname;

    // Step 1: Remove old item
    const removeBody = JSON.stringify({
      id: oldLineKey,
      quantity: 0,
      sections,
      sections_url: sectionsUrl
    });

    fetch(Theme.routes.cart_change_url, fetchConfig('json', { body: removeBody }))
      .then(res => res.json())
      .then((removeResponse) => {
        if (removeResponse.errors) {
          throw new Error(removeResponse.errors);
        }

        // Step 2: Add new variant with selling plan
        const addBody = JSON.stringify({
          items: [{
            id: newVariantId,
            quantity,
            selling_plan: sellingPlanId
          }],
          sections,
          sections_url: sectionsUrl
        });

        return fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: addBody }));
      })
      .then(res => res.json())
      .then(addResponse => {
        if (addResponse.errors || addResponse.status === 422) {
          throw new Error(addResponse.description || addResponse.errors || 'Failed to add variant');
        }

        this.dispatchEvent(new CartUpdateEvent({}, this.sectionId, {
          itemCount: addResponse.item_count,
          source: 'subscription-swap',
          sections: addResponse.sections
        }));

        morphSection(this.sectionId, addResponse.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error('[Cart] Subscription swap failed:', error);

        // Revert dropdown to previous selection
        if (select && previousOptionKey) {
          const previousOption = Array.from(select.options).find(
            opt => opt.value === previousOptionKey
          );
          if (previousOption) {
            select.value = previousOptionKey;
            this.#syncSubscriptionPickerState(select);
          }
        }

        // Show user-friendly error
        alert('Unable to update subscription option. Please try again.');
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }

  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
